// "Ajustes e regras já criados" da tela de Desembolso sob a política Ampli: as regras de
// pagamento, os grupos de insumos e os ajustes de mês que a obra já tem, no bloco que a
// moldura (`DisbursementWorkspace`) põe ACIMA da EAP.
//
// É um bloco de LEITURA e de atalho para o editor: cada linha abre o editor que já
// existe (regra, grupo) ou remove a regra (D22) ou o ajuste. Nada aqui calcula desembolso — os números
// (cobertura, valor aplicado, excedente) vêm do `GET .../disbursement/rules`.
//
// Fica visível com a EAP carregando ou vazia: depende só da lista de regras, que é a outra
// leitura da tela. Sem nada criado, declara a ausência no lugar onde a lista apareceria.
//
// O cabeçalho do bloco é o lugar das ações de CRIAÇÃO (organizar com IA, nova regra, novo
// grupo): ficam junto do que produzem, e o resumo do topo continua só informando.
import { useState } from "react";

import { quotaNote } from "./ai/useAiQuota";

import { ruleText, referenceChangedSince } from "./ruleText";
import { competenceText, money } from "./format";
import { levelOf } from "./hierarchy";
import { adjustmentSummary, adjustmentsByNode, monthsText } from "./monthEdit";
import { nodeLevelAcceptsRule } from "./RuleTargetPicker";

/**
 * `money()` lê o contexto do projeto por dentro (um hook disfarçado), então cada linha é
 * um componente: a contagem de hooks do bloco não pode depender do tamanho das listas.
 */
const RuleRow = ({ rule, readOnly, onEditRule, originName, removing, onRemoveRule, removeConsequence }) => {
  // D22: "Remover" pede confirmação ali mesmo, dizendo o que o alvo passa a seguir.
  const [confirming, setConfirming] = useState(false);
  return (
    <li className="ampli-created__row" data-kind="rule">
      <div className="ampli-created__main">
        <strong>{rule.target?.label ?? rule.target?.key}</strong>
        <span className="ampli-created__detail">{ruleText(rule, originName)}</span>
        {rule.active === false ? <span className="badge">inativa</span> : null}
        {referenceChangedSince(rule) ? (
          <span className="warning" data-testid={`reference-changed-${rule.ruleId}`}>
            {`A origem mudou desde o ajuste: era ${money(rule.reference.totalCents)}.`}
          </span>
        ) : null}
        {confirming ? (
          <span className="ampli-created__detail" role="status">{removeConsequence(rule)}</span>
        ) : null}
      </div>
      <span className="ampli-created__value">{money(rule.coveredCents)}</span>
      {!readOnly ? (
        <div className="ampli-created__actions">
          {confirming ? (
            <>
              <button
                type="button"
                className="button button-ghost small"
                disabled={removing}
                onClick={() => setConfirming(false)}
              >
                Manter
              </button>
              <button
                type="button"
                className="button button-error small"
                disabled={removing}
                onClick={async () => {
                  await onRemoveRule?.(rule);
                  setConfirming(false);
                }}
              >
                {removing ? "Removendo…" : "Confirmar remoção"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="button button-outline small"
                onClick={() => onEditRule?.(rule)}
              >
                Editar regra
              </button>
              {onRemoveRule ? (
                <button
                  type="button"
                  className="button button-error small"
                  aria-label={`Remover a regra de ${rule.target?.label ?? rule.target?.key}`}
                  onClick={() => setConfirming(true)}
                >
                  Remover
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </li>
  );
};

const GroupRow = ({ group, readOnly, onEditGroup }) => (
  <li className="ampli-created__row" data-kind="group">
    <div className="ampli-created__main">
      <strong>{group.name}</strong>
      <span className="ampli-created__detail">
        {group.memberCount == null
          ? "Insumos indisponíveis"
          : `${group.memberCount.toLocaleString("pt-BR")} insumos`}
      </span>
    </div>
    <span className="ampli-created__value">{money(group.coveredCents)}</span>
    {!readOnly ? (
      <button
        type="button"
        className="button button-outline small"
        onClick={() => onEditGroup?.(group)}
      >
        Editar grupo
      </button>
    ) : null}
  </li>
);

const MOVES_SHOWN = 3;

/**
 * O ajuste de mês de UM alvo, como um item só (D20): as entradas ativas do nó — as do ajuste
 * mês a mês e as do recorte de meses — resumidas em meses alterados e valor movido. Editar
 * abre o modo mês a mês do editor (só etapa, subetapa e serviço); remover desativa todas.
 */
const TargetAdjustmentRow = ({
  target, readOnly, removing, onEdit, onRemove,
}) => {
  const summary = adjustmentSummary(target.entries);
  const base = target.rule
    ? `Regra: ${ruleText(target.rule, target.originName)}`
    : target.followsLabel;
  const adjusted = `ajustado em ${monthsText(summary.monthCount)}`;
  const moves = target.entries.slice(0, MOVES_SHOWN)
    .map((entry) => `${competenceText(entry.fromCompetence)} → ${competenceText(entry.toCompetence)}`);
  const hidden = target.entries.length - moves.length;
  return (
    <li className="ampli-created__row" data-kind="adjustment" data-testid={`target-adjustment-${target.wbsCodeId}`}>
      <div className="ampli-created__main">
        <strong>{target.label}</strong>
        <span className="ampli-created__detail">
          {base ? `${base}, ${adjusted}` : `${adjusted.charAt(0).toUpperCase()}${adjusted.slice(1)}`}
        </span>
        <span className="ampli-created__detail">
          {`${moves.join("; ")}${hidden > 0 ? ` e mais ${hidden}` : ""}`}
        </span>
        {summary.exceedsCents > 0 ? (
          <span className="warning">
            {`${money(summary.exceedsCents)} não couberam no mês de origem.`}
          </span>
        ) : null}
      </div>
      <span className="ampli-created__value">{money(summary.movedCents)}</span>
      {!readOnly ? (
        <div className="ampli-created__actions">
          {target.editable && onEdit ? (
            <button
              type="button"
              className="button button-outline small"
              onClick={() => onEdit(target)}
            >
              Editar ajuste
            </button>
          ) : null}
          <button
            type="button"
            className="button button-error small"
            disabled={removing}
            onClick={() => onRemove?.(target)}
          >
            {removing ? "Removendo…" : "Remover"}
          </button>
        </div>
      ) : null}
    </li>
  );
};

/** O nível do nó na EAP, pela raiz mais longa que o prefixa. */
const nodeLevelOf = (wbsCodeId, roots) => {
  const root = [...(roots ?? [])]
    .filter((candidate) => String(wbsCodeId).startsWith(candidate))
    .sort((left, right) => right.length - left.length)[0];
  return root ? levelOf(wbsCodeId, root) : null;
};

// Cada seção recolhe sozinha: numa obra com uma regra por etapa a lista é longa.
const Section = ({ title, count, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <section className="ampli-created__section">
      <h4 className="ampli-created__section-title">
        <button
          type="button"
          className="ampli-created__section-toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span aria-hidden="true">{open ? "▾" : "▸"}</span>
          {` ${title} (${count.toLocaleString("pt-BR")})`}
        </button>
      </h4>
      {open ? <ul>{children}</ul> : null}
    </section>
  );
};

// Busca sem acento e sem caixa: "mao de obra" acha "MÃO DE OBRA".
const searchable = (value) => String(value ?? "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase();

// Acima disto o bloco abre recolhido: a EAP fica à vista sem rolar a lista inteira.
const COLLAPSED_ABOVE = 8;

const countText = (count, one, many) => `${count.toLocaleString("pt-BR")} ${count === 1 ? one : many}`;

const AmpliCreatedBlock = ({
  items = [],
  loaded = true,
  readOnly = false,
  onEditRule,
  onEditGroup,
  // D20: abre o editor no modo mês a mês para o alvo (`{ kind, key, level, label }`) e
  // remove todos os ajustes de um alvo (`wbsCodeId`).
  onEditAdjustment,
  onRemoveTargetAdjustment,
  // "Segue o desembolso do ERP": o que o alvo segue quando não tem regra própria.
  followsLabel = null,
  // O nome da origem ("Desembolso do ERP"), que o ajuste mês a mês nomeia (D21), e o que
  // o alvo de uma regra removida passa a seguir ("o desembolso do ERP").
  originName = null,
  followsObject = null,
  // D22: remove (desativa com histórico) uma regra.
  onRemoveRule,
  // As raízes da obra, para saber o nível de cada nó ajustado.
  roots = [],
  onNewRule,
  onNewGroup,
  onOrganizeWithAi,
  aiQuota = null,
}) => {
  const [removing, setRemoving] = useState(null);
  const [removingRule, setRemovingRule] = useState(null);
  // Recolher/expandir e buscar (revisão de 2026-10-07). `null` = automático: recolhido
  // quando há muitos itens.
  const [expandedChoice, setExpandedChoice] = useState(null);
  const [query, setQuery] = useState("");
  const removeRule = async (rule) => {
    if (removingRule) return;
    setRemovingRule(rule.ruleId);
    try {
      await onRemoveRule?.(rule);
    } finally {
      setRemovingRule(null);
    }
  };
  const removeConsequence = (rule) => (rule.target?.kind === "node"
    ? `Remover? "${rule.target?.label ?? rule.target?.key}" volta a seguir a regra de um nível acima, se houver, ou ${followsObject ?? "a origem"}.`
    : `Remover? Cada serviço com "${rule.target?.label ?? rule.target?.key}" volta a seguir a sua regra de EAP ou a origem.`);
  const rules = items.filter((item) => item.kind === "rule");
  const groups = items.filter((item) => item.kind === "group");
  const adjustments = items.filter(
    (item) => item.kind === "adjustment" && item.active !== false,
  );
  const nodeRules = new Map(
    rules
      .filter((rule) => rule.target?.kind === "node" && rule.active !== false)
      .map((rule) => [rule.target.key, rule]),
  );
  const adjustedTargets = [...adjustmentsByNode(adjustments)].map(([wbsCodeId, entries]) => {
    const rule = nodeRules.get(`node:${wbsCodeId}`) ?? null;
    const level = nodeLevelOf(wbsCodeId, roots);
    return {
      wbsCodeId,
      entries,
      rule,
      level,
      label: entries.find((entry) => entry.label)?.label ?? rule?.target?.label ?? wbsCodeId,
      followsLabel,
      originName,
      editable: nodeLevelAcceptsRule(level),
    };
  });

  const remove = async (target) => {
    if (removing) return;
    setRemoving(target.wbsCodeId);
    try {
      await onRemoveTargetAdjustment?.(target.wbsCodeId);
    } finally {
      setRemoving(null);
    }
  };

  const total = rules.length + groups.length + adjustedTargets.length;
  const expanded = expandedChoice ?? total <= COLLAPSED_ABOVE;
  const needle = searchable(query.trim());
  const matches = (...values) => needle === "" || values.some((value) => searchable(value).includes(needle));
  const shownRules = rules.filter((rule) => matches(
    rule.target?.label, rule.target?.key, rule.description, ruleText(rule, originName),
  ));
  const shownGroups = groups.filter((group) => matches(group.name));
  const shownAdjusted = adjustedTargets.filter((target) => matches(target.label, target.wbsCodeId));
  const shown = shownRules.length + shownGroups.length + shownAdjusted.length;
  const summary = [
    countText(rules.length, "regra", "regras"),
    countText(groups.length, "grupo", "grupos"),
    countText(adjustedTargets.length, "ajuste de mês", "ajustes de mês"),
  ].join(" · ");

  return (
    <div className="ampli-created">
      <header className="ampli-created__header">
        <div className="ampli-created__heading">
          <h3 className="ampli-created__title">Ajustes e regras já criados</h3>
          <p className="ampli-created__subtitle">
            Regras, grupos e ajustes que mudam a curva da obra.
          </p>
        </div>
        {!readOnly ? (
          <div className="ampli-created__toolbar">
            <div className="ampli-created__ai-action">
              <button
                type="button"
                className="button button-accent small"
                data-testid="disbursement-organize-ai"
                onClick={onOrganizeWithAi}
              >
                Organizar com IA
              </button>
              {quotaNote(aiQuota) ? (
                <span
                  className="ampli-created__ai-quota"
                  data-testid="disbursement-ai-quota"
                  title="A cota de IA é compartilhada com as outras telas da obra."
                >
                  {quotaNote(aiQuota)}
                </span>
              ) : null}
            </div>
            <button
              type="button"
              className="button button-outline small"
              onClick={onNewRule}
            >
              Nova regra
            </button>
            <button
              type="button"
              className="button button-outline small"
              onClick={onNewGroup}
            >
              Novo grupo
            </button>
          </div>
        ) : null}
      </header>
      {loaded && total > 0 ? (
        <div className="ampli-created__controls">
          <button
            type="button"
            className="button button-ghost small"
            aria-expanded={expanded}
            aria-controls="ampli-created-list"
            onClick={() => setExpandedChoice(!expanded)}
          >
            {expanded ? "Recolher" : "Expandir"}
          </button>
          <span className="ampli-created__summary" data-testid="created-summary">{summary}</span>
          {expanded ? (
            <label className="ampli-created__search">
              <span className="visually-hidden">Buscar nas regras, grupos e ajustes</span>
              <input
                type="search"
                className="default-input"
                placeholder="Buscar regra, grupo ou ajuste"
                aria-label="Buscar nas regras, grupos e ajustes"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </label>
          ) : null}
          {expanded && needle !== "" ? (
            <span className="ampli-created__summary" role="status">
              {`${shown.toLocaleString("pt-BR")} de ${total.toLocaleString("pt-BR")}`}
            </span>
          ) : null}
        </div>
      ) : null}
      {!loaded ? <p className="card-label">Carregando…</p> : null}
      {loaded && expanded && total > 0 && shown === 0 ? (
        <p className="empty-state">Nenhuma regra, grupo ou ajuste encontrado para a busca.</p>
      ) : null}
      {loaded && rules.length + groups.length + adjustments.length === 0 ? (
        <p className="empty-state">
          Nenhuma regra, grupo ou ajuste foi criado nesta obra ainda.
        </p>
      ) : null}
      {expanded ? (
      <div id="ampli-created-list" className="ampli-created__list">
      {shownRules.length > 0 ? (
        <Section title="Regras de pagamento" count={shownRules.length}>
          {shownRules.map((rule) => (
            <RuleRow
              key={rule.rowId}
              rule={rule}
              readOnly={readOnly}
              onEditRule={onEditRule}
              originName={originName}
              removing={removingRule === rule.ruleId}
              onRemoveRule={onRemoveRule ? removeRule : undefined}
              removeConsequence={removeConsequence}
            />
          ))}
        </Section>
      ) : null}
      {shownGroups.length > 0 ? (
        <Section title="Grupos de insumos" count={shownGroups.length}>
          {shownGroups.map((group) => (
            <GroupRow
              key={group.rowId}
              group={group}
              readOnly={readOnly}
              onEditGroup={onEditGroup}
            />
          ))}
        </Section>
      ) : null}
      {shownAdjusted.length > 0 ? (
        <Section title="Ajustes de mês" count={shownAdjusted.length}>
          {shownAdjusted.map((target) => (
            <TargetAdjustmentRow
              key={target.wbsCodeId}
              target={target}
              readOnly={readOnly}
              removing={removing === target.wbsCodeId}
              onEdit={
                onEditAdjustment
                  ? () => onEditAdjustment({
                    kind: "node",
                    key: `node:${target.wbsCodeId}`,
                    level: target.level,
                    label: target.label,
                  })
                  : undefined
              }
              onRemove={remove}
            />
          ))}
        </Section>
      ) : null}
      </div>
      ) : null}
    </div>
  );
};

export default AmpliCreatedBlock;
